import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import {
  getLlmBatchJob,
  type LlmBatchJob,
  type LlmSuggestion,
} from "@/api/llm";

const TERMINAL_STATUSES = new Set(["completed", "failed", "expired", "cancelled"]);

export default function AdminLlmJobDetail() {
  const { batchJobId = "" } = useParams<{ batchJobId: string }>();

  const detailQ = useQuery({
    queryKey: ["llm-batch-job", batchJobId],
    queryFn: async () => {
      const r = await getLlmBatchJob(batchJobId);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    enabled: Boolean(batchJobId),
    refetchInterval: (q) => {
      const status = q.state.data?.batch_job?.status;
      return status && !TERMINAL_STATUSES.has(status) ? 5000 : false;
    },
  });

  if (detailQ.isLoading) {
    return <div className="mt-empty">Загружаем…</div>;
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <div className="mt-empty mt-empty--error">
        {(detailQ.error as Error | undefined)?.message ?? "Ошибка"}
      </div>
    );
  }

  const { batch_job: job, suggestions } = detailQ.data;
  const completed = suggestions.filter((s) => s.status === "completed").length;
  const failed = suggestions.filter((s) => s.status === "failed").length;
  const pending = suggestions.filter((s) => s.status === "pending").length;

  return (
    <div className="rf-shell">
      <main className="rf-main">
        <div className="rf-content">
          <header className="rf-head">
            <Link to="/admin/llm-jobs" className="mt-btn mt-btn--sm mt-btn--ghost">
              ← Все jobs
            </Link>
            <h1>Batch job {job.batch_job_id.slice(0, 8)}</h1>
          </header>

          <JobMeta job={job} />

          <div className="rf-section">
            <div className="rf-section__head">
              <span>Прогресс</span>
              <span className="mt-toolbar__sep" style={{ flex: 1 }} />
              <span className="mt-text-tight mt-muted">
                {completed} done · {failed} failed · {pending} pending
              </span>
            </div>
            <div className="rf-section__body">
              <div className="rf-progress__bar">
                <i
                  style={{
                    width: `${
                      job.item_count
                        ? Math.round(((completed + failed) / job.item_count) * 100)
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="rf-section">
            <div className="rf-section__head">
              <span>Suggestions</span>
              <span className="mt-toolbar__sep" style={{ flex: 1 }} />
              <span className="mt-text-tight mt-muted">{suggestions.length}</span>
            </div>
            <div className="rf-section__body">
              {suggestions.map((s) => (
                <SuggestionRow key={s.suggestion_id} suggestion={s} />
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function JobMeta({ job }: { job: LlmBatchJob }) {
  return (
    <div className="rf-section">
      <div className="rf-section__head">
        <span>Метаданные</span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span className={badgeClassForStatus(job.status)}>{job.status}</span>
      </div>
      <div className="rf-section__body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(160px, max-content) 1fr",
            gap: "4px 12px",
            fontSize: "var(--fs-sm)",
          }}
        >
          <Cell k="Provider">{job.provider}</Cell>
          <Cell k="Model">{job.model}</Cell>
          <Cell k="Provider batch ID">{job.provider_batch_id ?? "—"}</Cell>
          <Cell k="Items">{job.item_count}</Cell>
          <Cell k="Requested by">{job.requested_by}</Cell>
          <Cell k="Created">{job.created_at}</Cell>
          <Cell k="Updated">{job.updated_at}</Cell>
          {job.completed_at && <Cell k="Completed">{job.completed_at}</Cell>}
        </div>
      </div>
    </div>
  );
}

function Cell({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <>
      <div className="mt-mono mt-text-tight mt-muted">{k}</div>
      <div style={{ overflowWrap: "anywhere" }}>{children}</div>
    </>
  );
}

function SuggestionRow({ suggestion }: { suggestion: LlmSuggestion }) {
  return (
    <div className="mt-llm-card">
      <div className="mt-llm-card__head">
        <span className={badgeClassForStatus(suggestion.status as never)}>
          {suggestion.status}
        </span>
        <span className="mt-mono mt-text-tight">
          {suggestion.suggestion_id.slice(0, 8)}
        </span>
        <span>·</span>
        <Link
          to={`/admin/review/focus/${encodeURIComponent(suggestion.review_item_id)}`}
        >
          {suggestion.review_item_id.slice(0, 8)}
        </Link>
        <span>·</span>
        <span>{suggestion.model}</span>
      </div>
      {suggestion.error_message && (
        <div className="mt-llm-card__reason" style={{ color: "var(--red-700)" }}>
          {suggestion.error_message}
        </div>
      )}
    </div>
  );
}

function badgeClassForStatus(status: string): string {
  if (status === "completed") return "mt-badge mt-badge--success mt-badge--no-dot";
  if (status === "failed" || status === "expired") {
    return "mt-badge mt-badge--error mt-badge--no-dot";
  }
  if (status === "cancelled") return "mt-badge mt-badge--neutral mt-badge--no-dot";
  return "mt-badge mt-badge--info mt-badge--no-dot";
}
